import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchOrderBook, selectOrderBookByOutcomeId } from '@/store/markets'
import useOrderBookRealTime from '@/hooks/useOrderBookRealTime'
import constants from '@/constants'

/**
 * Custom hook to manage orderbook fetching and subscriptions for an outcome
 */
export const useOutcomeOrderBook = ({
  outcome,
  question,
  parentQuestion,
  activeCurrency,
  isAccordion,
  isMultiOutcomeMarket,
  isAccordionOpen,
  position,
  setPosition,
  setActiveTabIndex,
}) => {
  const dispatch = useDispatch()

  // Get orderbooks for both positions
  const orderbook = useSelector(selectOrderBookByOutcomeId(outcome?.id, activeCurrency?.id, position))
  const longOrderbook = useSelector(selectOrderBookByOutcomeId(outcome?.id, activeCurrency?.id, constants.position.LONG))
  const shortOrderbook = useSelector(
    selectOrderBookByOutcomeId(outcome?.id, activeCurrency?.id, constants.position.SHORT)
  )

  // Orderbook subscriptions for real-time updates
  const orderBookSubscriptions = useMemo(() => {
    if (
      !isAccordion ||
      !isMultiOutcomeMarket ||
      !isAccordionOpen ||
      !outcome?.id ||
      !activeCurrency?.id ||
      outcome?.status !== 'active'
    ) {
      return []
    }

    const currency =
      activeCurrency?.id === 'OOM' ? 'OOM' : question?.canonical_currency || parentQuestion?.canonical_currency

    // Subscribe to both positions for real-time updates
    return [
      {
        marketId: question?.id || parentQuestion?.id,
        currency: currency,
        outcome: outcome.id,
        positionType: constants.position.LONG,
      },
      {
        marketId: question?.id || parentQuestion?.id,
        currency: currency,
        outcome: outcome.id,
        positionType: constants.position.SHORT,
      },
    ]
  }, [
    isAccordion,
    isMultiOutcomeMarket,
    isAccordionOpen,
    outcome?.id,
    outcome?.status,
    activeCurrency?.id,
    question?.id,
    parentQuestion?.id,
    question?.canonical_currency,
    parentQuestion?.canonical_currency,
  ])

  useOrderBookRealTime(orderBookSubscriptions)

  // Fetch orderbooks when accordion opens
  useEffect(() => {
    if (isAccordion && isMultiOutcomeMarket && isAccordionOpen && outcome?.id && activeCurrency?.id) {
      const marketQuestion = question || parentQuestion

      if (marketQuestion?.id && marketQuestion?.order_book_enabled && outcome?.status === 'active') {
        // Reset position to LONG when accordion opens
        if (setPosition) setPosition(constants.position.LONG)
        if (setActiveTabIndex) setActiveTabIndex(0)

        // Fetch orderbook for both positions when accordion opens
        dispatch(
          fetchOrderBook({
            question: marketQuestion.id,
            currency_mode: activeCurrency.id === 'OOM' ? 'play_money' : 'real_money',
            outcome: outcome.id,
            position: constants.position.LONG,
            source: 'outcome_accordion',
            showErrorToast: true,
          })
        )
        dispatch(
          fetchOrderBook({
            question: marketQuestion.id,
            currency_mode: activeCurrency.id === 'OOM' ? 'play_money' : 'real_money',
            outcome: outcome.id,
            position: constants.position.SHORT,
            source: 'outcome_accordion',
            showErrorToast: true,
          })
        )
      }
    }
  }, [
    isAccordion,
    isMultiOutcomeMarket,
    isAccordionOpen,
    outcome?.id,
    outcome?.status,
    activeCurrency?.id,
    question?.id,
    question?.order_book_enabled,
    parentQuestion?.id,
    parentQuestion?.order_book_enabled,
    dispatch,
    setPosition,
    setActiveTabIndex,
  ])

  // Also fetch orderbook when position changes (for tab switching)
  useEffect(() => {
    if (isAccordion && isMultiOutcomeMarket && isAccordionOpen && outcome?.id && position && activeCurrency?.id) {
      const marketQuestion = question || parentQuestion

      if (marketQuestion?.id && marketQuestion?.order_book_enabled && outcome?.status === 'active') {
        dispatch(
          fetchOrderBook({
            question: marketQuestion.id,
            currency_mode: activeCurrency.id === 'OOM' ? 'play_money' : 'real_money',
            outcome: outcome.id,
            position,
            source: 'outcome_accordion_tab',
            showErrorToast: true,
          })
        )
      }
    }
  }, [
    position,
    isAccordion,
    isMultiOutcomeMarket,
    isAccordionOpen,
    outcome?.id,
    outcome?.status,
    activeCurrency?.id,
    question?.id,
    question?.order_book_enabled,
    parentQuestion?.id,
    parentQuestion?.order_book_enabled,
    dispatch,
  ])

  return {
    orderbook,
    longOrderbook,
    shortOrderbook,
  }
}

